import { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { AlertTriangle, Shield, ExternalLink, ChevronDown, Star } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  SPECIES_FACTS,
  THREATS,
  GALLERY_IMAGES,
  BEHAVIORS,
} from '@/lib/index';

// ---- animation helpers ----
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] } },
};
const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

function useSection() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return { ref, inView };
}

function scrollToSection(id: string) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth' });
}

// ============================================================
// HERO SECTION
// ============================================================
function HeroSection() {
  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage:
            'url(https://images.unsplash.com/photo-1538099130811-745e64318258?w=1800&q=85)',
        }}
      />
      {/* Dark gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />

      {/* Content */}
      <div className="relative z-10 text-center text-white px-6 max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          <Badge className="mb-6 bg-primary/80 text-primary-foreground border-primary/30 text-xs font-mono tracking-widest px-4 py-1.5">
            IUCN 濒危物种 · Ailurus fulgens
          </Badge>

          <h1 className="text-5xl md:text-7xl font-bold leading-tight mb-6 tracking-tight">
            遇见
            <br />
            <span className="text-primary drop-shadow-lg">小熊猫</span>
          </h1>

          <p className="text-lg md:text-xl text-white/80 leading-relaxed max-w-2xl mx-auto mb-10 font-light">
            它们是喜马拉雅山麓的红色精灵，蓬松的尾巴、温柔的眼神，
            却正面临着栖息地消失的严峻威胁。
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              onClick={() => scrollToSection('about')}
              className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-8 py-6 text-base font-medium shadow-xl hover:shadow-2xl transition-all duration-300"
            >
              深入了解
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => scrollToSection('action')}
              className="border-white/40 text-white hover:bg-white/10 rounded-full px-8 py-6 text-base font-medium backdrop-blur-sm"
            >
              参与保护
            </Button>
          </div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-10 left-1/2 -translate-x-1/2 text-white/60 flex flex-col items-center gap-2 cursor-pointer"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, y: [0, 8, 0] }}
        transition={{ delay: 1.5, duration: 2, repeat: Infinity }}
        onClick={() => scrollToSection('about')}
      >
        <span className="text-xs tracking-widest uppercase">探索</span>
        <ChevronDown className="w-5 h-5" />
      </motion.div>
    </section>
  );
}

// ============================================================
// ABOUT SECTION
// ============================================================
function AboutSection() {
  const { ref, inView } = useSection();

  return (
    <section id="about" ref={ref} className="py-24 bg-background">
      <div className="container mx-auto px-6">
        {/* Intro */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          className="text-center mb-16"
        >
          <Badge variant="secondary" className="mb-4 text-xs tracking-wider font-mono">
            ABOUT THE SPECIES
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold mb-5">
            认识<span className="text-primary">小熊猫</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto leading-relaxed">
            小熊猫是小熊猫科（Ailuridae）中唯一现存的物种，既非熊也非浣熊，而是拥有独立地位的神奇哺乳动物。
          </p>
        </motion.div>

        {/* Two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate={inView ? 'visible' : 'hidden'}
            transition={{ delay: 0.2 }}
          >
            <div className="relative rounded-3xl overflow-hidden aspect-[4/3] shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1530048265464-939ec434a884?w=800&q=80"
                alt="小熊猫"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-6">
                <p className="text-white font-mono text-sm">
                  学名：<em>Ailurus fulgens</em>
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate={inView ? 'visible' : 'hidden'}
            className="space-y-6"
          >
            <motion.p variants={fadeUp} className="text-foreground/80 text-lg leading-loose">
              小熊猫体型约与家猫相仿，但躯干更长、尾巴更粗。那一身标志性的红棕色毛发，在寒冷的喜马拉雅冬日里格外鲜艳，宛如一团跃动的火焰。
            </motion.p>
            <motion.p variants={fadeUp} className="text-foreground/80 text-lg leading-loose">
              它们主要分布在不丹、中国、印度、缅甸和尼泊尔的温带山地森林中，海拔通常在 1500 至 4800 米之间。
            </motion.p>
            <motion.p variants={fadeUp} className="text-muted-foreground text-base leading-loose">
              目前小熊猫被 IUCN 列为濒危（Endangered）物种，野外总数量估计不足
              <span className="text-destructive font-semibold"> 10,000 只</span>，且仍在持续减少。
            </motion.p>
          </motion.div>
        </div>

        {/* Facts Grid */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          className="grid grid-cols-2 md:grid-cols-3 gap-4"
        >
          {SPECIES_FACTS.map((fact) => (
            <motion.div key={fact.label} variants={fadeUp}>
              <Card className="border border-border/60 hover:border-primary/30 hover:shadow-md transition-all duration-300 bg-card">
                <CardContent className="p-5 text-center">
                  <div className="text-3xl mb-3">{fact.icon}</div>
                  <p className="text-xs text-muted-foreground font-mono tracking-wider uppercase mb-1">
                    {fact.label}
                  </p>
                  <p className="font-semibold text-foreground text-sm">{fact.value}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ============================================================
// BEHAVIOR SECTION
// ============================================================
function BehaviorSection() {
  const { ref, inView } = useSection();

  return (
    <section
      id="behavior"
      ref={ref}
      className="py-24"
      style={{
        background:
          'linear-gradient(160deg, oklch(0.97 0.012 30) 0%, oklch(0.95 0.015 145 / 0.2) 100%)',
      }}
    >
      <div className="container mx-auto px-6">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          className="text-center mb-16"
        >
          <Badge variant="secondary" className="mb-4 text-xs tracking-wider font-mono">
            LIFESTYLE & BEHAVIOR
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold mb-5">
            生活<span className="text-primary">习性</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto leading-relaxed">
            在云雾缭绕的山地森林里，小熊猫有着一套独特的生存智慧。
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-20">
          {BEHAVIORS.map((b, i) => (
            <motion.div
              key={b.title}
              variants={fadeUp}
              initial="hidden"
              animate={inView ? 'visible' : 'hidden'}
              transition={{ delay: i * 0.1 }}
            >
              <Card className="h-full border border-border/50 bg-background/80 hover:shadow-lg hover:border-primary/20 transition-all duration-300">
                <CardContent className="p-7">
                  <div className="text-4xl mb-4">{b.emoji}</div>
                  <h3 className="text-xl font-bold mb-3 text-foreground">{b.title}</h3>
                  <p className="text-muted-foreground leading-relaxed text-sm">{b.description}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Feature image */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          className="rounded-3xl overflow-hidden relative aspect-[21/8] shadow-2xl"
        >
          <img
            src="https://images.unsplash.com/photo-1715883436095-a1c883e7d18e?w=1400&q=80"
            alt="小熊猫吃竹子"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent flex items-center">
            <div className="p-10 max-w-lg">
              <p className="text-white text-2xl font-bold leading-snug mb-3">
                "假拇指"的秘密
              </p>
              <p className="text-white/80 text-sm leading-relaxed">
                小熊猫的腕骨中有一块特化的桡侧籽骨，功能类似拇指，
                让它们能轻松抓握竹茎——与大熊猫有着趋同进化的奇妙巧合。
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ============================================================
// CONSERVATION SECTION
// ============================================================
function ConservationSection() {
  const { ref, inView } = useSection();

  return (
    <section id="conservation" ref={ref} className="py-24 bg-background">
      <div className="container mx-auto px-6">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          className="text-center mb-16"
        >
          <Badge className="mb-4 bg-destructive/10 text-destructive border-destructive/20 text-xs tracking-wider font-mono">
            CONSERVATION STATUS · ENDANGERED
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold mb-5">
            保护<span className="text-destructive">现状</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto leading-relaxed">
            小熊猫面临着来自多个方向的威胁，了解它们正是保护行动的第一步。
          </p>
        </motion.div>

        {/* Status bar */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          className="rounded-2xl bg-gradient-to-r from-destructive/5 to-destructive/10 border border-destructive/20 p-8 mb-12 flex flex-col md:flex-row items-center gap-6"
        >
          <AlertTriangle className="w-12 h-12 text-destructive flex-shrink-0" />
          <div>
            <h3 className="text-xl font-bold text-destructive mb-2">IUCN 红色名录：濒危（EN）</h3>
            <p className="text-muted-foreground leading-relaxed text-sm">
              小熊猫野外种群数量在过去 18 年间（约三代繁殖周期）已减少超过 50%，
              若威胁因素不能得到有效遏制，其数量将持续下滑至危险水平。
            </p>
          </div>
        </motion.div>

        {/* Threats grid */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-16"
        >
          {THREATS.map((threat) => (
            <motion.div key={threat.title} variants={fadeUp}>
              <Card
                className={`h-full border transition-all duration-300 hover:shadow-md ${
                  threat.severity === 'high'
                    ? 'border-destructive/30 bg-destructive/5'
                    : 'border-border/60 bg-card'
                }`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div
                      className={`mt-1 w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                        threat.severity === 'high' ? 'bg-destructive' : 'bg-primary'
                      }`}
                    />
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-bold text-foreground">{threat.title}</h3>
                        <Badge
                          variant={threat.severity === 'high' ? 'destructive' : 'secondary'}
                          className="text-xs"
                        >
                          {threat.severity === 'high' ? '高度威胁' : '中度威胁'}
                        </Badge>
                      </div>
                      <p className="text-muted-foreground text-sm leading-relaxed">
                        {threat.description}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>

        {/* Hope panel */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          className="rounded-3xl bg-accent/10 border border-accent/20 p-10 flex flex-col md:flex-row items-center gap-8"
        >
          <Shield className="w-14 h-14 text-accent flex-shrink-0" />
          <div>
            <h3 className="text-xl font-bold text-foreground mb-3 flex items-center gap-2">
              <Star className="w-5 h-5 text-accent" />
              保护行动已见成效
            </h3>
            <p className="text-muted-foreground leading-relaxed text-sm">
              红熊猫网络（Red Panda Network）等机构正积极推动栖息地保护与社区共建项目，
              通过建立野生动物走廊、推行可持续农业和教育推广，让当地居民成为保护小熊猫的重要力量。
              中国、尼泊尔等国也不断扩大自然保护区范围，为小熊猫创造更安全的家园。
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ============================================================
// GALLERY SECTION
// ============================================================
function GallerySection() {
  const { ref, inView } = useSection();
  const [selected, setSelected] = useState<number | null>(null);

  return (
    <section
      id="gallery"
      ref={ref}
      className="py-24"
      style={{ background: 'oklch(0.12 0.015 30)' }}
    >
      <div className="container mx-auto px-6">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          className="text-center mb-16"
        >
          <Badge className="mb-4 bg-white/10 text-white/70 border-white/10 text-xs tracking-wider font-mono">
            PHOTO GALLERY
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold mb-5 text-white">
            影像<span className="text-primary">画廊</span>
          </h2>
          <p className="text-white/50 text-lg max-w-xl mx-auto">
            每一张照片，都是小熊猫存在于这片星球上的珍贵印记。
          </p>
        </motion.div>

        {/* Bento Grid */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          className="grid grid-cols-2 md:grid-cols-3 gap-4"
        >
          {GALLERY_IMAGES.map((img, i) => (
            <motion.div
              key={img.url}
              variants={fadeUp}
              className={`relative overflow-hidden rounded-2xl cursor-pointer group ${
                i === 0 ? 'md:col-span-2 md:row-span-2' : ''
              }`}
              style={{ aspectRatio: i === 0 ? '16/9' : '4/3' }}
              onClick={() => setSelected(selected === i ? null : i)}
            >
              <img
                src={img.url}
                alt={img.alt}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300 flex items-end">
                <div className="p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                  <p className="text-white text-sm font-medium">{img.caption}</p>
                </div>
              </div>
              {selected === i && (
                <div className="absolute inset-0 ring-4 ring-primary rounded-2xl" />
              )}
            </motion.div>
          ))}
        </motion.div>

        <motion.p
          variants={fadeUp}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          className="text-center text-white/30 text-xs mt-8 font-mono"
        >
          All photos via Unsplash · Free to use under Unsplash License
        </motion.p>
      </div>
    </section>
  );
}

// ============================================================
// ACTION SECTION
// ============================================================
function ActionSection() {
  const { ref, inView } = useSection();

  const actions = [
    {
      emoji: '🌐',
      title: '关注红熊猫网络',
      desc: '国际权威保护组织，持续推动小熊猫野外种群恢复工作。',
      link: 'https://redpandanetwork.org',
      label: '访问官网',
    },
    {
      emoji: '📢',
      title: '传播保护意识',
      desc: '将小熊猫的故事分享给更多人，每一次分享都是一份力量。',
      link: 'https://www.iucnredlist.org/species/714/45195924',
      label: '了解更多',
    },
    {
      emoji: '🏫',
      title: '支持教育项目',
      desc: '为栖息地周边社区的孩子提供环境教育，是最长远的保护投资。',
      link: 'https://redpandanetwork.org/programs/',
      label: '了解项目',
    },
  ];

  return (
    <section
      id="action"
      ref={ref}
      className="py-28 relative overflow-hidden"
    >
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage:
            'url(https://images.unsplash.com/photo-1600094329163-a2b52f913831?w=1400&q=70)',
        }}
      />
      <div className="absolute inset-0 bg-foreground/85" />

      <div className="relative z-10 container mx-auto px-6">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
            它们需要<br />
            <span className="text-primary">你的关注</span>
          </h2>
          <p className="text-white/70 text-lg max-w-xl mx-auto leading-relaxed">
            保护小熊猫不需要身处喜马拉雅，从现在、从这里开始就可以。
          </p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto"
        >
          {actions.map((a) => (
            <motion.div key={a.title} variants={fadeUp}>
              <Card className="bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300 h-full">
                <CardContent className="p-7 flex flex-col h-full">
                  <div className="text-4xl mb-4">{a.emoji}</div>
                  <h3 className="text-white font-bold text-lg mb-3">{a.title}</h3>
                  <p className="text-white/60 text-sm leading-relaxed flex-1 mb-5">{a.desc}</p>
                  <a
                    href={a.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-primary text-sm font-medium hover:gap-3 transition-all duration-200"
                  >
                    {a.label}
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>

        <motion.p
          variants={fadeUp}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          className="text-center text-white/40 text-xs mt-12 font-mono"
        >
          野外不足 10,000 只 · 每一只都弥足珍贵
        </motion.p>
      </div>
    </section>
  );
}

// ============================================================
// HOME PAGE
// ============================================================
export default function Home() {
  return (
    <>
      <HeroSection />
      <AboutSection />
      <BehaviorSection />
      <ConservationSection />
      <GallerySection />
      <ActionSection />
    </>
  );
}
